import { create, v, w } from '@dojo/framework/core/vdom';
import { focus } from '@dojo/framework/core/middleware/focus';
import { i18n } from '@dojo/framework/core/middleware/i18n';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import Icon from '@dojo/widgets/icon';
import commonBundle from './nls';
import { formatAriaProperties, Keys } from '@dojo/widgets/common/util';
import { isVariantTitle } from '../utils';
import css from '../css/tab-container.m.css';
const factory = create({
    focus,
    i18n,
    icache: createICacheMiddleware(),
    theme
})
    .properties()
    .children();
export const TabContainer = factory(({ children, id, middleware: { focus, i18n, icache, theme }, properties }) => {
    const { alignButtons = 'top', aria = {}, initialActiveIndex = 0, onActiveIndex, onClose, xqa } = properties();
    const { activeIndex: userIndex } = properties();
    const themeCss = theme.classes(css);
    const { messages } = i18n.localize(commonBundle);
    const activeIndex = icache.getOrSet('activeIndex', userIndex ? userIndex : initialActiveIndex);
    const tabs = children().map((val, index) => {
        if (isVariantTitle(val.title)) {
            if (index === activeIndex) {
                return val.title.open;
            }
            return val.title.closed;
        }
        return val.title;
    });
    function closeTab(index) {
        onClose && onClose(index);
        setActiveIndex(0);
    }
    function setActiveIndex(index) {
        focus.focus();
        onActiveIndex && onActiveIndex(index);
        icache.set('activeIndex', index);
    }
    function onKeyDown(event, { closeable, disabled }, index) {
        event.stopPropagation();
        const total = tabs.length;
        switch (event.which) {
            case Keys.Escape:
                if (closeable && !disabled) {
                    closeTab(index);
                }
                break;
            case Keys.Left:
            case Keys.Up:
                event.preventDefault();
                setActiveIndex((index - 1 + total) % total);
                break;
            case Keys.Right:
            case Keys.Down:
                event.preventDefault();
                setActiveIndex((index + 1) % total);
                break;
            case Keys.Home:
                event.preventDefault();
                setActiveIndex(0);
                break;
            case Keys.End:
                event.preventDefault();
                setActiveIndex(tabs.length - 1);
                break;
        }
    }
    const renderTab = (tab, index) => {
        const { closeable, disabled } = tab;
        const active = index === activeIndex;
        let { title } = tab;
        if (isVariantTitle(title)) {
            title = active ? title.open : title.closed;
        }
        const name = Array.isArray(title) ? title : [title];
        return v('div', {
            ...formatAriaProperties({
                controls: `${id}-tabpanel-${index}`,
                disabled: disabled ? 'true' : 'false',
                selected: active ? 'true' : 'false'
            }),
            classes: [
                themeCss.tabButton,
                active ? themeCss.activeTabButton : null,
                closeable ? themeCss.closeable : null,
                disabled ? themeCss.disabledTabButton : null
            ],
            focus: active ? focus.shouldFocus : () => false,
            id: `${id}-tabbutton-${index}`,
            key: `${index}-tabbutton`,
            onclick: () => {
                if (!disabled) {
                    setActiveIndex(index);
                }
            },
            onkeydown: (event) => onKeyDown(event, tab, index),
            role: 'tab',
            tabIndex: active ? 0 : -1
        }, [
            v('span', { key: 'tabButtonContent', classes: [themeCss.tabButtonContent] }, [
                ...name,
                closeable
                    ? v('button', {
                        disabled,
                        tabIndex: active ? 0 : -1,
                        classes: [themeCss.close],
                        key: `${index}-tabbutton-close`,
                        type: 'button',
                        onclick: (event) => {
                            event.stopPropagation();
                            if (!disabled) {
                                closeTab(index);
                            }
                        }
                    }, [w(Icon, { type: 'closeIcon', altText: messages.close, size: 'small' })])
                    : null,
                v('span', { classes: [themeCss.indicator, active && themeCss.indicatorActive] }, [
                    v('span', { classes: [themeCss.indicatorContent] })
                ])
            ])
        ]);
    };
    const content = [
        v('div', { key: 'buttons', classes: [themeCss.tabButtons] }, children().map(renderTab)),
        v('div', { key: 'tabs', classes: [themeCss.tabs] }, children().map((child, index) => {
            const { disabled, displayInactive, content: contentval } = child;
            const active = activeIndex === index && !disabled;
            const content = active || displayInactive ? (Array.isArray(contentval) ? contentval : [contentval]) : [];
            return v('div', {
                id: `${id}-tabpanel-${index}`,
                ...formatAriaProperties({ labelledby: `${id}-tabbutton-${index}` }),
                role: 'tabpanel',
                classes: [active ? themeCss.tab : undefined],
                hidden: !active
            }, content);
        }))
    ];
    let alignClass;
    let orientation = 'horizontal';
    switch (alignButtons) {
        case 'right':
            alignClass = themeCss.alignRight;
            orientation = 'vertical';
            content.reverse();
            break;
        case 'bottom':
            alignClass = themeCss.alignBottom;
            content.reverse();
            break;
        case 'left':
            alignClass = themeCss.alignLeft;
            orientation = 'vertical';
            break;
    }
    return v('div', {
        ...formatAriaProperties(Object.assign(aria, { orientation: orientation })),
        key: 'root',
        xqa,
        classes: [alignClass || null, themeCss.root],
        role: 'tablist'
    }, [...content]);
});
export default TabContainer;
